// Frontend client for the backend /api proxy (see server/). All calls degrade
// gracefully to empty results so callers can fall back to the bundled mock data
// whenever the proxy has no API key configured or a provider is unreachable.

import type { Quote } from './coingecko';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) || '';

async function get<T>(path: string, fallback: T): Promise<T> {
  try {
    const res = await fetch(`${BASE}${path}`, { headers: { accept: 'application/json' } });
    if (!res.ok) throw new Error(String(res.status));
    return (await res.json()) as T;
  } catch (err) {
    if (import.meta.env.DEV) console.warn(`[api] ${path} failed:`, err);
    return fallback;
  }
}

/** Real US equity/ETF quotes via the proxy (Finnhub). {} when unavailable. */
export function fetchStockQuotes(symbols: string[]): Promise<Record<string, Quote>> {
  if (!symbols.length) return Promise.resolve({});
  return get(`/api/quotes?symbols=${encodeURIComponent(symbols.join(','))}`, {} as Record<string, Quote>);
}

/** Thai/Asian/global quotes via the proxy (Yahoo, keyless). Keyed by Yahoo symbol. */
export function fetchIntlQuotes(yahooSymbols: string[]): Promise<Record<string, Quote>> {
  if (!yahooSymbols.length) return Promise.resolve({});
  return get(`/api/yquotes?symbols=${encodeURIComponent(yahooSymbols.join(','))}`, {} as Record<string, Quote>);
}

/** Spot gold. Yahoo has no spot ticker, so this has its own upstream. null when down. */
export function fetchGoldSpot(): Promise<(Quote & { prevClose: number | null }) | null> {
  return get('/api/goldspot', null as (Quote & { prevClose: number | null }) | null);
}

/** Daily News deep analysis via the proxy (Claude). Throws on failure so the UI can show state. */
export async function analyzeNews(item: { headline: string; source?: string; tag?: string; time?: string; summary?: string }): Promise<any> {
  const res = await fetch(`${BASE}/api/analyze`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(item),
  });
  if (!res.ok) {
    const err: any = new Error(`analyze ${res.status}`);
    try { err.body = await res.json(); } catch { /* ignore */ }
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/** Global-situation band above the Daily News feed (keyless feeds, see server/worldpulse.mjs). */
export interface WorldPulse {
  at: number; score: number; band: string; stale: boolean;
  parts: { k: string; th: string; en: string; value: string; score: number; weight: number; note: string }[];
  themes: { id: string; th: string; en: string; spike: number }[];
  quakes: { count: number; big: number; max: { mag: number; place: string; at: number; tsunami: boolean } | null } | null;
  hazards: { total: number; cats: { id: string; th: string; en: string; count: number }[] } | null;
  read: { th: string; en: string };
  sources: string[];
}
export function fetchWorld(): Promise<WorldPulse | null> {
  return get(`/api/world`, null as WorldPulse | null);
}

/** The AI read of the world, generated server-side on a 3h clock. */
export interface WorldBrief {
  headline: string; brief: string;
  drivers: { t: string; d: string; dir: 'up' | 'down' | 'flat' }[];
  watch: { a: string; d: string }[];
  invalidate: string;
  provider?: string; at?: number; stale?: boolean;
}
/** Resolves to `{ error }` rather than throwing — the panel shows the reason. */
export async function fetchWorldBrief(): Promise<WorldBrief | { error: string }> {
  try {
    const res = await fetch(`${BASE}/api/world/brief`, { headers: { accept: 'application/json' } });
    const body = await res.json();
    if (!res.ok) return { error: String(body?.error || res.status) };
    return body as WorldBrief;
  } catch (err) {
    return { error: String((err as Error)?.message || 'network') };
  }
}

export interface NewsItem { t: string; src: string; time: string; tag: string; impact: string; url?: string; datetime?: number; }
export function fetchNews(limit = 12): Promise<NewsItem[]> {
  return get(`/api/news?limit=${limit}`, [] as NewsItem[]);
}

/** Real historical OHLC candles via the proxy (Yahoo). [] when unavailable.
 *  Pass either an MT5 timeframe `{ tf: 'H4' }` or `{ range, interval }`. */
export function fetchOhlc(symbol: string, params: { tf?: string; range?: string; interval?: string }): Promise<any[]> {
  if (!symbol) return Promise.resolve([]);
  const qs = params.tf
    ? `tf=${encodeURIComponent(params.tf)}`
    : `range=${encodeURIComponent(params.range || '3mo')}&interval=${encodeURIComponent(params.interval || '1d')}`;
  return get(`/api/ohlc?symbol=${encodeURIComponent(symbol)}&${qs}`, [] as any[]);
}

// Calendars resolve to null on failure, not [] - "the request failed" and "no
// events in this window" are different answers and the pages say different
// things for them. (The hook used to fill both with bundled sample rows.)
export function fetchEarnings(from: string, to: string): Promise<any[] | null> {
  return get(`/api/calendar/earnings?from=${from}&to=${to}`, null as any[] | null);
}
export function fetchIpo(from: string, to: string): Promise<any[] | null> {
  return get(`/api/calendar/ipo?from=${from}&to=${to}`, null as any[] | null);
}
export function fetchDividends(from: string, to: string): Promise<any[] | null> {
  return get(`/api/calendar/dividends?from=${from}&to=${to}`, null as any[] | null);
}
/** This week's economic releases, all countries (server/econcalendar.mjs). null on failure. */
export interface EconEvent { at: number; country: string; flag: string; title: string; impact: number; holiday: boolean; forecast: string; previous: string }
export function fetchEconWeek(): Promise<{ ok: boolean; coverage: string; events: EconEvent[] } | null> {
  return get('/api/calendar/econ', null as { ok: boolean; coverage: string; events: EconEvent[] } | null);
}

/** Real options Open-Interest chain via the proxy (Yahoo). null when unavailable. */
export function fetchOptions(symbol: string, date?: number | null): Promise<any> {
  if (!symbol) return Promise.resolve(null);
  return get(`/api/options?symbol=${encodeURIComponent(symbol)}${date ? `&date=${date}` : ''}`, null);
}

export interface Fundamental { price: number; chg: number; vol: number; mcap: number | null; pe: number | null; eps: number | null; yield: number | null; cur: string | null; }
/** Real fundamentals (price/mcap/PE/EPS/div-yield) via the proxy (Yahoo, keyless). Keyed by Yahoo symbol; {} when unavailable. */
// The server answers at most 150 symbols per call and drops the rest. The
// earnings market-cap filter asks for up to 600, so in earnings season most
// companies came back with no cap and "Large ≥ $10B" hid them. Batches of 150,
// one after another, merged.
export async function fetchFundamentals(yahooSymbols: string[]): Promise<Record<string, Fundamental>> {
  if (!yahooSymbols.length) return {};
  const out: Record<string, Fundamental> = {};
  for (let i = 0; i < yahooSymbols.length; i += 150) {
    const part = yahooSymbols.slice(i, i + 150);
    Object.assign(out, await get(`/api/fundamentals?symbols=${encodeURIComponent(part.join(','))}`, {} as Record<string, Fundamental>));
  }
  return out;
}

export interface Financials {
  annual?: { endDate: string; revenue: number; netIncome: number }[];
  keyStats?: { priceToBook: number | null; forwardPE: number | null };
  financialData?: { revenueGrowth: number | null; earningsGrowth: number | null; operatingMargins: number | null; profitMargins: number | null; returnOnEquity: number | null };
}
/** One company in depth: annual statements, P/BV, ROE, margins (Yahoo, keyless). */
export function fetchFinancials(yahooSymbol: string): Promise<Financials> {
  if (!yahooSymbol) return Promise.resolve({});
  return get(`/api/financials?symbol=${encodeURIComponent(yahooSymbol)}`, {} as Financials);
}

export interface FedPolicy {
  funds?: { upper: number; lower: number; mid: number; effective: number | null; asOf: string } | null;
  macro?: {
    realYield10y?: { value: number; asOf: string } | null;
    cpiYoY?: { value: number; asOf: string } | null;
    corePceYoY?: { value: number; asOf: string } | null;
  };
  dots?: { year: number; median: number; low: number | null; high: number | null }[];
  path?: { date: string; sep: boolean; contract: string; method: string; before: number; implied: number; moveBps: number; cut: number; hold: number; hike: number }[];
}
/** Fed target range, dot plot and market-implied meeting odds (keyless). */
export function fetchFed(): Promise<FedPolicy> {
  return get('/api/fed', {} as FedPolicy);
}

export interface MacroRegime {
  quadrant?: 'goldilocks' | 'reflation' | 'stagflation' | 'deflation';
  label?: { th: string; en: string };
  favours?: string[];
  against?: string[];
  note?: string;
  growth?: { score: number; up: boolean; parts: { k: string; th: string; en: string; v: number }[] };
  inflation?: { score: number; up: boolean; yoy: number | null; parts: { k: string; th: string; en: string; v: number }[] };
  curve?: { t10y2y: number | null; t10y3m: number | null; asOf: string | null; inverted: boolean };
}
/** Growth-vs-inflation quadrant with its components (FRED, keyless). */
export function fetchRegime(): Promise<MacroRegime> {
  return get('/api/regime', {} as MacroRegime);
}

export interface CotRow {
  sym: string; th: string; market: string; date: string;
  openInterest: number; nonComm: number; comm: number; small: number;
  weekChange: number; pct: number | null; extreme: 'crowded-long' | 'crowded-short' | null;
  weeks: number; min: number; max: number;
}
/** CFTC Commitments of Traders positioning with a three-year percentile. */
export function fetchCot(): Promise<{ asOf: string | null; rows: CotRow[] }> {
  return get('/api/cot', { asOf: null, rows: [] as CotRow[] });
}

export interface FxRow {
  code: string; th: string; series: string; asOf: string;
  value: number; mean: number; deviation: number; z: number; yoy: number | null;
  verdict: 'rich' | 'fair' | 'cheap'; months: number;
}
/** Real effective exchange rates against their own ten-year average (BIS). */
export function fetchFx(): Promise<{ asOf: string | null; base?: string; rows: FxRow[] }> {
  return get('/api/fx', { asOf: null, rows: [] as FxRow[] });
}

/** Technical + latest-quarter screen, keyed by the Yahoo symbol asked for. */
export function fetchScreen(yahooSymbols: string[]): Promise<Record<string, any>> {
  if (!yahooSymbols.length) return Promise.resolve({});
  return get(`/api/screen?symbols=${encodeURIComponent(yahooSymbols.join(','))}`, {} as Record<string, any>);
}

/** MT5 account snapshots pushed by the user's MQL5 EA. */
export function fetchMt5Accounts(): Promise<any[]> {
  return get('/api/mt5/accounts', [] as any[]);
}

// ---- Catalog: Academy courses + EA downloads (public read) ----
export interface Course {
  id: string; cat: string; title: string; desc: string; inst: string; rating: number;
  reviews: number; hours: number; lessons: number; price: number; sale: number;
  level: string; tag?: string; featured?: boolean; url?: string; video?: string; published?: boolean; sort?: number;
  /** middle price tier of a Technical class (self = sale, vip = price) */
  cohort?: number;
  /** uploaded cover image URL (cache-busted server-side) */
  cover?: string;
  /** published episode-video count — public listings only */
  epVideos?: number;
  /** published study-material count (files + links) */
  materials?: number;
  /** enrolled-student count — present on admin listings only */
  students?: number;
}
/** One episode video of a catalog course. `url` is empty on the public list
 *  for non-free episodes unless the viewer is enrolled (server-gated). */
export interface CourseEpVideo { id: number; courseId: string; ep: number; title: string; url: string; free: boolean; published: boolean; }
export const fetchCourseEpVideos = (courseId: string) =>
  get(`/api/course/${encodeURIComponent(courseId)}/videos`, [] as CourseEpVideo[]);
/** One study material of a catalog course: an uploaded file or a link. `href`
 *  is null when the viewer has not paid for this course (`locked`); for a file
 *  it is the gated download route, never a path to the file itself. There is
 *  no free item - the server opens materials to paying students only. */
export interface CourseMaterial {
  id: number; courseId: string; title: string; kind: 'file' | 'link';
  fileName: string | null; ext: string | null; size: number; url: string | null;
  published: boolean; sort: number; updated: number;
  locked?: boolean; href?: string | null;
}
export const fetchCourseMaterials = (courseId: string) =>
  get(`/api/course/${encodeURIComponent(courseId)}/materials`, [] as CourseMaterial[]);
/** What an upload may be. The server enforces the same list and the same cap. */
export const MATERIAL_ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.zip,.png,.jpg,.jpeg,.webp';
export const MATERIAL_MAX_BYTES = 25 * 1024 * 1024;
/** 2.4 MB / 830 KB, the size shown next to a download. */
export const fmtBytes = (n: number) =>
  n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
export interface Ea { id: number; name: string; version: string; desc: string; file: string; status: string; published?: boolean; }
export function fetchCourses(): Promise<Course[]> { return get('/api/courses', [] as Course[]); }
/**
 * Same data, but a failure REJECTS instead of resolving to [].
 *
 * The shop needs the difference: `get()` folds a network error into an empty
 * array, so "nothing published yet" and "the catalogue did not load" arrive
 * identical and the page cannot tell the customer which one they are seeing.
 */
export async function fetchCoursesStrict(): Promise<Course[]> {
  const res = await fetch(`${BASE}/api/courses`, { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(String(res.status));
  return await res.json() as Course[];
}
/** The Technical 19-Class rows (tech-bundle + tech-c1..19) — editable in the
 *  back office, hidden from the catalogue grid, read here by the curriculum. */
export function fetchTechClasses(): Promise<Course[]> { return get('/api/tech-classes', [] as Course[]); }
export function fetchEas(): Promise<Ea[]> { return get('/api/eas', [] as Ea[]); }

// ---- Technical 19-Class course: per-episode videos (attached by admin) ----
export interface ClassVideo {
  id: number; classNo: number; ep: number; title: string; url: string;
  free: boolean; published?: boolean; updated?: number;
  /** true when the viewer may see the lesson exists but not play it - url is '' */
  locked?: boolean;
}
/** Published episode videos for the Technical curriculum (public read). */
export function fetchClassVideos(): Promise<ClassVideo[]> { return get('/api/course/videos', [] as ClassVideo[]); }
export const adminListClassVideos = () => get('/api/admin/course-videos', null as ClassVideo[] | null);
export const adminSaveClassVideo = (v: Partial<ClassVideo>) => adminSend<ClassVideo>('/api/admin/course-videos', 'POST', v);
export const adminDeleteClassVideo = (id: number) => adminSend<{ ok: boolean }>(`/api/admin/course-videos/${id}`, 'DELETE');

// ---- Enrollments: which student bought which class ----
export interface Enrollment {
  id: number; userId: number; courseId: string; paid: number; progress: number; note: string;
  created: number; email: string; userName: string; courseTitle: string;
  /** Watched / published episodes, and the first one still unwatched. Computed
   *  server-side from ep_progress, not the dead enrollments.progress column. */
  epsDone?: number; epsTotal?: number; nextEp?: number | null;
}
/** The logged-in user's own purchases (empty when logged out). */
/** What the signed-in member owns. null on failure - NOT [] - so a failed read
 *  is never mistaken for "owns nothing", which put a price and an Enrol button
 *  back on classes the student had already paid for. */
export function fetchMyCourses(): Promise<Enrollment[] | null> { return get('/api/my/courses', null as Enrollment[] | null); }

// ---- Lesson progress ----
export interface CourseProgress {
  watched: number[]; done: number; total: number; pct: number; nextEp: number | null;
}
const NO_PROGRESS: CourseProgress = { watched: [], done: 0, total: 0, pct: 0, nextEp: null };

export function fetchMyProgress(courseId: string): Promise<CourseProgress> {
  return get(`/api/my/progress?courseId=${encodeURIComponent(courseId)}`, NO_PROGRESS);
}

/** Mark one episode watched (or un-watched). Resolves to the course's new
 *  standing, or null if the server refused — the caller keeps its own state
 *  rather than showing a tick that was never saved. */
export async function markEpWatched(courseId: string, ep: number, watched = true): Promise<CourseProgress | null> {
  try {
    const res = await fetch(`${BASE}/api/my/progress`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ courseId, ep, watched }),
    });
    if (!res.ok) return null;
    return await res.json() as CourseProgress;
  } catch { return null; }
}

async function adminSend<T>(path: string, method: string, body?: any): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    // The server explains itself in the body ({ error: 'duplicate', twinRef }, 'approved',
    // 'user_not_found'...), and the back office has a message for each - which it could
    // never show, because this threw a bare Error and e.body was always undefined.
    const e: any = new Error(`${method} ${path} ${res.status}`);
    e.status = res.status;
    try { e.body = await res.json(); } catch { /* not JSON */ }
    throw e;
  }
  return res.json();
}
export const adminListCourses = () => get('/api/admin/courses', null as Course[] | null);
/** A course category. `saved` false means no settings row yet — it exists only
 *  because some course carries the name, typo included. `courses` is how many
 *  use it, which is what makes a stray one obvious in the back office. */
export interface CourseCat { name: string; color: string | null; hidden: boolean; sort: number; courses: number; saved: boolean }
/** Public: names, colours and which to leave out of the Academy filter row. */
export function fetchCourseCats(): Promise<Pick<CourseCat, 'name' | 'color' | 'hidden'>[]> {
  return get('/api/course-cats', [] as Pick<CourseCat, 'name' | 'color' | 'hidden'>[]);
}
export const adminListCourseCats = () => get('/api/admin/course-cats', null as CourseCat[] | null);
export const adminSaveCourseCat = (c: Partial<CourseCat>) => adminSend<CourseCat>('/api/admin/course-cats', 'POST', c);
/** Renames on the courses too, so nothing is left pointing at the old name. */
export const adminRenameCourseCat = (name: string, rename: string) =>
  adminSend<CourseCat>('/api/admin/course-cats', 'POST', { name, rename });
/** Drops the settings only; the courses keep the category. */
export const adminDeleteCourseCat = (name: string) =>
  adminSend<{ ok: boolean }>(`/api/admin/course-cats/${encodeURIComponent(name)}`, 'DELETE');

export const adminSaveCourse = (c: Partial<Course>) => adminSend<Course>('/api/admin/courses', 'POST', c);
export const adminDeleteCourse = (id: string) => adminSend<{ ok: boolean }>(`/api/admin/courses/${encodeURIComponent(id)}`, 'DELETE');
export const adminUploadCourseCover = (id: string, image: string, filename: string) =>
  adminSend<Course>(`/api/admin/courses/${encodeURIComponent(id)}/cover`, 'POST', { image, filename });
export const adminListCourseEpVideos = (courseId: string) =>
  get(`/api/admin/courses/${encodeURIComponent(courseId)}/videos`, null as CourseEpVideo[] | null);
export const adminSaveCourseEpVideo = (courseId: string, v: Partial<CourseEpVideo>) =>
  adminSend<CourseEpVideo>(`/api/admin/courses/${encodeURIComponent(courseId)}/videos`, 'POST', v);
export const adminDeleteCourseEpVideo = (id: number) => adminSend<{ ok: boolean }>(`/api/admin/course-ep-videos/${id}`, 'DELETE');
export const adminListCourseMaterials = (courseId: string) =>
  get(`/api/admin/courses/${encodeURIComponent(courseId)}/materials`, null as CourseMaterial[] | null);
/** Creates a link (no id), or edits any row's title, link, order and flags. */
export const adminSaveCourseMaterial = (courseId: string, m: Partial<CourseMaterial>) =>
  adminSend<CourseMaterial>(`/api/admin/courses/${encodeURIComponent(courseId)}/materials`, 'POST', m);
export const adminDeleteCourseMaterial = (id: number) => adminSend<{ ok: boolean }>(`/api/admin/course-materials/${id}`, 'DELETE');
/**
 * Uploads a file as the raw request body. XHR rather than fetch for its
 * progress events: a 20 MB deck on a home uplink takes long enough that a
 * button with no feedback gets clicked twice. Rejects with the same
 * "METHOD path status" message adminSend uses, so callers read the code the
 * same way.
 */
export function adminUploadCourseMaterial(courseId: string, file: File,
  meta: { title?: string; published?: boolean },
  onProgress?: (pct: number) => void): Promise<CourseMaterial> {
  const path = `/api/admin/courses/${encodeURIComponent(courseId)}/materials/upload`;
  const qs = new URLSearchParams({
    name: file.name, title: meta.title || '',
    published: meta.published === false ? '0' : '1',
  });
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('POST', `${BASE}${path}?${qs}`);
    x.setRequestHeader('content-type', 'application/octet-stream');
    x.setRequestHeader('accept', 'application/json');
    if (onProgress) x.upload.onprogress = e => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    x.onload = () => {
      if (x.status < 200 || x.status >= 300) return reject(new Error(`POST ${path} ${x.status}`));
      try { resolve(JSON.parse(x.responseText) as CourseMaterial); }
      catch { reject(new Error(`POST ${path} bad_json`)); }
    };
    x.onerror = () => reject(new Error(`POST ${path} network`));
    x.send(file);
  });
}
export const adminListEas = () => get('/api/admin/eas', null as Ea[] | null);
export const adminSaveEa = (e: Partial<Ea>) => adminSend<Ea>('/api/admin/eas', 'POST', e);
export const adminDeleteEa = (id: number) => adminSend<{ ok: boolean }>(`/api/admin/eas/${id}`, 'DELETE');

// ---- EA library: the 45 Premium AI builds ↔ files on the server machine ----
export interface EaBuildFileInfo { name: string; size: number; mtime: number; }
export interface EaBuildRow {
  id: string; series: string; seriesName: string; name: string; ver: string;
  dir: string; engine: string; magic: number; tags: string[]; symbols: string[]; price: number;
  featured: boolean; blurb: string; hasHints: boolean;
  file: {
    mq5: EaBuildFileInfo | null; ex5: EaBuildFileInfo | null;
    /** legacy single preset */
    set: EaBuildFileInfo | null;
    /** every preset attached to this build */
    presets?: { id: number; name: string; size: number; mtime: number }[];
    source: string | null; note: string | null; published: boolean; updated: number;
  } | null;
}
export interface EaScanFile { path: string; name: string; ext: 'mq5' | 'ex5' | 'set'; size: number; mtime: number; }
export interface EaSuggestion { path: string; name: string; ex5: string | null; size: number; mtime: number; score: number; }
export const adminListEaBuilds = () => get('/api/admin/ea-builds', null as EaBuildRow[] | null);
export const adminEaScan = (force = false) =>
  get(`/api/admin/ea-scan${force ? '?force=1' : ''}`, null as { files: EaScanFile[]; suggestions: Record<string, EaSuggestion> } | null);
export const adminEaAutoAttach = () =>
  adminSend<{ attached: string[]; failed: { buildId: string; error: string }[] }>('/api/admin/ea-builds/auto-attach', 'POST');
export const adminEaAttach = (buildId: string, mq5?: string, ex5?: string) =>
  adminSend(`/api/admin/ea-builds/${buildId}/attach`, 'POST', { mq5, ex5 });
export const adminEaAttachGenerated = (buildId: string) =>
  adminSend(`/api/admin/ea-builds/${buildId}/generated`, 'POST');
export const adminEaUpload = (buildId: string, name: string, dataB64: string) =>
  adminSend(`/api/admin/ea-builds/${buildId}/upload`, 'POST', { name, data: dataB64 });
export const adminEaDetach = (buildId: string) =>
  adminSend<{ ok: boolean }>(`/api/admin/ea-builds/${buildId}/detach`, 'POST');
export const adminEaPatch = (buildId: string, patch: { published?: boolean; note?: string }) =>
  adminSend(`/api/admin/ea-builds/${buildId}`, 'PATCH', patch);
// ---- Track Port catalog DB: admin-edited overrides over src/premiumAI.ts ----
// A missing field means "use the code default"; the storefront merges per field.
export interface EaCatalogOv {
  buildId: string; updated?: number;
  name?: string; ver?: string; blurb?: string; price?: number;
  featured?: boolean; hidden?: boolean;
  net?: number; pf?: number; win?: number; trades?: number; dd?: number; months?: number;
}
export const fetchEaCatalog = () => get('/api/ea/catalog', [] as EaCatalogOv[]);
export const adminListEaCatalog = () => get('/api/admin/ea-catalog', null as EaCatalogOv[] | null);
export const adminSaveEaCatalog = (patch: Partial<EaCatalogOv> & { buildId: string }) =>
  adminSend<EaCatalogOv>('/api/admin/ea-catalog', 'POST', patch);
export const adminResetEaCatalog = (buildId: string) =>
  adminSend<{ ok: boolean }>(`/api/admin/ea-catalog/${buildId}`, 'DELETE');

// ---- EA purchases: buyer records per build (admin) + "my builds" (user) ----
export interface EaPurchase {
  id: number; userId: number; buildId: string; paid: number; machines: number;
  note: string; created: number; email: string; userName: string;
}
export const adminListEaPurchases = (buildId?: string) =>
  get(`/api/admin/ea-purchases${buildId ? `?build_id=${encodeURIComponent(buildId)}` : ''}`,
    null as { counts: Record<string, number>; purchases: EaPurchase[] } | null);
export const adminAddEaPurchase = (body: { email: string; buildId: string; paid?: number; machines?: number; note?: string }) =>
  adminSend<EaPurchase>('/api/admin/ea-purchases', 'POST', body);
export const adminDeleteEaPurchase = (id: number) =>
  adminSend<{ ok: boolean }>(`/api/admin/ea-purchases/${id}`, 'DELETE');
/** Build ids the signed-in user owns ([] when logged out). */
export const fetchMyEaBuilds = () => get('/api/ea/mine', [] as string[]);

// ---- EAs added by hand from the back office (join the catalog at runtime) ----
export interface EaCustomBuild {
  id: string; name: string; ver: string; dir: 'BUY' | 'BOTH' | 'SWITCH';
  price: number; blurb: string; tags: string[]; symbols: string[];
  magic: number; featured: boolean; custom: true; created: number; updated: number;
}
export const fetchEaCustom = () => get('/api/ea/custom', [] as EaCustomBuild[]);
export const adminSaveEaCustom = (b: Partial<EaCustomBuild>) =>
  adminSend<EaCustomBuild>('/api/admin/ea-custom', 'POST', b);
export const adminDeleteEaCustom = (id: string) =>
  adminSend<{ ok: boolean }>(`/api/admin/ea-custom/${id}`, 'DELETE');

/** Public: which published builds have a downloadable .ex5 (customers never
 *  see .mq5 source — that stays in the back office). */
export interface EaPreset { id: number; name: string; size: number; }
export interface EaPublicBuild {
  id: string; ex5: boolean;
  /** legacy single preset (pre many-per-build) */ set?: boolean;
  /** every .set this build ships */ presets?: EaPreset[];
}
export const fetchEaBuilds = () => get('/api/ea/builds', [] as EaPublicBuild[]);
export const eaDownloadUrl = (buildId: string, kind: 'mq5' | 'ex5' | 'core' | 'set' = 'ex5', presetId?: number) =>
  `/api/ea/download/${buildId}?kind=${kind}${presetId ? `&preset=${presetId}` : ''}`;
export const adminAddEnrollment = (body: { email: string; courseId: string; paid?: number; note?: string }) =>
  adminSend<Enrollment>('/api/admin/enrollments', 'POST', body);
export const adminDeleteEnrollment = (id: number) => adminSend<{ ok: boolean }>(`/api/admin/enrollments/${id}`, 'DELETE');

// ---- Admin back office (ADMIN_EMAILS-gated; server returns 403 otherwise) ----
export interface AdminUser {
  id: number; email: string; name: string; provider: string;
  plan: string; tier: string | null; billing: string | null;
  trialEnd: number | null; created: number; isAdmin: boolean; watch: number;
  /** birth data saved from the Human Design page, when the member chose to */
  birth?: string | null; btime?: string | null; place?: string | null;
  tz?: number | null; hdType?: string | null;
}
export interface AdminUserStats { total: number; premium: number; gold: number; trial: number; free: number; new7d: number; }
export function adminListUsers(q = ''): Promise<{ stats: AdminUserStats; users: AdminUser[] } | null> {
  return get(`/api/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`, null);
}
export async function adminGrantPlan(id: number, grant: 'free' | 'trial' | 'gold' | 'premium'): Promise<AdminUser> {
  const res = await fetch(`${BASE}/api/admin/users/${id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ grant }),
  });
  if (!res.ok) throw new Error(`grant ${res.status}`);
  return res.json();
}
export async function adminDeleteUser(id: number): Promise<void> {
  const res = await fetch(`${BASE}/api/admin/users/${id}`, { method: 'DELETE', headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`delete ${res.status}`);
}

// ---- MT5 market-data feed (charts + quotes from the user's terminals) ----
export interface Mt5Source {
  id: string; name: string; online: boolean; symbols?: number; lastSeenSec: number;
  /** the EA is posting (says nothing about whether the data moves) */
  pushing?: boolean;
  /** posting, but its ticks have stopped - a terminal that lost its connection */
  stale?: boolean;
  /** age of the newest tick this broker reported, seconds; null on an older EA */
  tickAgeSec?: number | null;
}
/** Brokers currently feeding data (optionally filtered to those carrying `symbol`). */
export function fetchMt5Sources(symbol?: string): Promise<Mt5Source[]> {
  return get(`/api/mt5/sources${symbol ? `?symbol=${encodeURIComponent(symbol)}` : ''}`, [] as Mt5Source[]);
}
/** Historical candles for one broker+symbol+timeframe. */
export function fetchMt5Ohlc(broker: string, symbol: string, tf: string): Promise<{ bars: any[]; live: boolean }> {
  return get(`/api/mt5/ohlc?broker=${encodeURIComponent(broker)}&symbol=${encodeURIComponent(symbol)}&tf=${encodeURIComponent(tf)}`, { bars: [], live: false });
}
/** Latest live quote for one broker+symbol. */
export function fetchMt5Quote(broker: string, symbol: string): Promise<Record<string, { price: number; chg: number; bid?: number; ask?: number; ageMs: number }>> {
  return get(`/api/mt5/prices?broker=${encodeURIComponent(broker)}&symbols=${encodeURIComponent(symbol)}`, {});
}

export function fetchMt5Quotes(broker: string, symbols: string[]): Promise<Record<string, { price: number; chg: number; bid?: number; ask?: number; ageMs: number }>> {
  if (!symbols.length) return Promise.resolve({});
  return get(`/api/mt5/prices?broker=${encodeURIComponent(broker)}&symbols=${encodeURIComponent(symbols.join(','))}`, {});
}

export interface Mt5All {
  quotes: Record<string, { price: number; chg: number; bid?: number; ask?: number; ageMs: number }>;
  total: number;
  capped: boolean;
}
/** Every live quote a broker is currently feeding (its whole Market Watch). */
export function fetchMt5All(broker: string, limit = 0): Promise<Mt5All> {
  if (!broker) return Promise.resolve({ quotes: {}, total: 0, capped: false });
  return get(`/api/mt5/all?broker=${encodeURIComponent(broker)}&limit=${limit}`, { quotes: {}, total: 0, capped: false });
}

/** Start a real Stripe Checkout for a plan. Returns { url } or { error }. */
export async function payCheckout(plan: string, email?: string): Promise<{ url?: string; error?: string }> {
  try {
    const res = await fetch(`${BASE}/api/pay/checkout`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ plan, email, origin: window.location.origin }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data?.error || String(res.status) };
    return { url: data.url };
  } catch (e) { return { error: String(e) }; }
}

/** Verify a returned Stripe Checkout session. */
export function payStatus(sessionId: string): Promise<{ paid: boolean; plan?: string; tier?: string; billing?: string; days?: number; recurring?: boolean }> {
  return get(`/api/pay/status?session_id=${encodeURIComponent(sessionId)}`, { paid: false });
}

// ---- PromptPay checkout (QR + slip, manual review) — plans and courses ----
export interface PayItem { plan?: string; courseId?: string; tier?: 'self' | 'cohort' | 'vip'; classNo?: number; buildId?: string; }
/** Bundle offer: every EA build for one price (vs. the list price of all of them). */
export interface EaBundleOffer { usd: number; count: number; listUsd: number; }
export const fetchEaBundle = () => get('/api/ea/bundle', null as EaBundleOffer | null);
export interface PromptPayCreated {
  ref?: string; amount?: number; payload?: string; name?: string; label?: string;
  free?: boolean; enrolled?: boolean; error?: string;
  /** true when the server handed back an order this account already had open */
  reused?: boolean;
  /** whether that reused order already carries a slip */
  slip?: boolean;
}
/** Create a pending PromptPay payment. kind = 'plan' | 'course' | 'ea' | 'ea_bundle'. */
export async function payPromptPay(kind: 'plan' | 'course' | 'ea' | 'ea_bundle', item: PayItem): Promise<PromptPayCreated> {
  try {
    const res = await fetch(`${BASE}/api/pay/promptpay`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ kind, item }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data?.error || String(res.status) };
    return data;
  } catch (e) { return { error: String(e) }; }
}
/** Upload a transfer slip (base64 data URI) for a pending payment ref. */
export async function payUploadSlip(ref: string, image: string, filename?: string): Promise<{ ok?: boolean; error?: string }> {
  try {
    const res = await fetch(`${BASE}/api/pay/slip`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ref, image, filename }),
    });
    const data = await res.json();
    if (!res.ok) return { error: data?.error || String(res.status) };
    return data;
  } catch (e) { return { error: String(e) }; }
}
export function payPromptPayStatus(ref: string): Promise<{ status: string; kind?: string; item?: any; amount?: number; hasSlip?: boolean }> {
  return get(`/api/pay/promptpay/status?ref=${encodeURIComponent(ref)}`, { status: 'unknown' });
}

// ---- admin: payment review queue ----
export interface AdminPayment {
  id: number; ref: string; userId: number; email: string; userName: string;
  kind: string; item: any; amount: number; currency: string; status: string;
  slip: string | null; note: string; created: number; reviewed: number | null; reviewedBy: string;
}
export interface PaymentStats {
  /** the review queue - every unreviewed slip, not scoped to a month */
  pending: number;
  /** decided within the current calendar month */
  approved: number; rejected: number; revenue: number;
  /** local-time midnight on the 1st, so the page can name the month it is showing */
  monthStart?: number;
  /** running totals since the shop opened */
  allApproved?: number; allRejected?: number; allRevenue?: number;
}
export function adminListPayments(status = ''): Promise<{ stats: PaymentStats; payments: AdminPayment[] } | null> {
  return get(`/api/admin/payments${status ? `?status=${encodeURIComponent(status)}` : ''}`, null);
}
export const adminReviewPayment = (id: number, action: 'approve' | 'reject') =>
  adminSend<{ status: string; already?: boolean }>(`/api/admin/payments/${id}`, 'POST', { action });
export const adminSlipUrl = (id: number) => `${BASE}/api/admin/payments/${id}/slip`;
/** Remove a payment that never took money. The server refuses approved ones. */
export const adminDeletePayment = (id: number) =>
  adminSend<{ ok: boolean; status?: string }>(`/api/admin/payments/${id}`, 'DELETE');

export async function fetchHealth(): Promise<{ ok: boolean; providers: Record<string, boolean | number> }> {
  return get('/api/health', { ok: false, providers: {} });
}

// ============ AUTH + PER-USER STATE ============
// Same-origin cookies flow automatically; `credentials: 'include'` covers the
// case where VITE_API_BASE points the API at a different origin.
import type { MmUser } from '../lib/premium';

export interface AuthInfo { user: MmUser | null; accounts: boolean; line: boolean; /** Google sign-in is configured server-side */ google?: boolean; /** card checkout is configured server-side */ stripe?: boolean; }
export interface UserState { watch: string[]; alerts: any[]; onboarded: boolean; }

async function authFetch<T>(path: string, init: RequestInit, fallback: T): Promise<T> {
  try {
    const res = await fetch(`${BASE}${path}`, { credentials: 'include', ...init });
    if (!res.ok) { const e: any = new Error(String(res.status)); e.status = res.status; try { e.body = await res.json(); } catch { /* */ } throw e; }
    return (await res.json()) as T;
  } catch (err) {
    if (import.meta.env.DEV) console.warn(`[auth] ${path} failed:`, err);
    throw err;
  }
}

const jsonInit = (method: string, body?: any): RequestInit => ({
  method, headers: { 'content-type': 'application/json', accept: 'application/json' },
  body: body ? JSON.stringify(body) : undefined,
});

/** Current session + whether server accounts / LINE login are available. */
export function fetchSession(): Promise<AuthInfo> {
  return get('/api/auth/session', { user: null, accounts: false, line: false, google: false, stripe: false } as AuthInfo);
}
export function authSignup(email: string, password: string, name: string): Promise<{ user: MmUser }> {
  return authFetch('/api/auth/signup', jsonInit('POST', { email, password, name }), { user: null as any });
}
export function authLogin(email: string, password: string): Promise<{ user: MmUser }> {
  return authFetch('/api/auth/login', jsonInit('POST', { email, password }), { user: null as any });
}
export function authLogout(): Promise<{ ok: boolean }> {
  return authFetch('/api/auth/logout', jsonInit('POST'), { ok: true });
}
export function saveProfile(patch: Record<string, any>): Promise<{ user: MmUser }> {
  return authFetch('/api/auth/profile', jsonInit('POST', patch), { user: null as any });
}
/** Set the profile picture from a data URI, or pass null to remove it. */
export async function saveAvatar(image: string | null): Promise<{ user: MmUser | null; error?: string }> {
  try {
    // credentials, like every other authenticated call: without it a split-origin
    // VITE_API_BASE deployment never sends the session cookie and this 401s.
    const res = await fetch(`${BASE}/api/auth/avatar`, { ...jsonInit('POST', { image }), credentials: 'include' });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { user: null, error: body.error || String(res.status) };
    return { user: body.user };
  } catch { return { user: null, error: 'network' }; }
}
/** Absolute URL to kick off LINE Login (full-page redirect). */
export function lineLoginUrl(): string { return `${BASE}/api/auth/line/start`; }
export function googleLoginUrl(): string { return `${BASE}/api/auth/google/start`; }

/** The member's saved watchlist/alerts. REJECTS on any failure - deliberately
 *  unlike get(): an empty fallback here was taken for the real lists, marked
 *  "synced", and saved straight back over what the server held, so one failed
 *  request (the API restarting, a dropped connection) wiped a member's
 *  watchlist and alerts. */
export async function fetchState(): Promise<UserState> {
  const res = await fetch(`${BASE}/api/state`, { headers: { accept: 'application/json' }, credentials: 'include' });
  if (!res.ok) throw new Error(`state ${res.status}`);
  const st = await res.json();
  return {
    watch: Array.isArray(st?.watch) ? st.watch : [],
    alerts: Array.isArray(st?.alerts) ? st.alerts : [],
    onboarded: !!st?.onboarded,
  };
}
export function saveState(state: Partial<UserState>): Promise<UserState> {
  return authFetch('/api/state', jsonInit('PUT', state), { watch: [], alerts: [], onboarded: false } as UserState);
}

// ---- Live per-EA results, keyed by magic number ----
export interface EaLiveStat {
  magic: number; net: number; win: number; trades: number;
  pf: number | null; dd: number; accounts: number; updated: number;
}
/** {} when no terminal is reporting per-build figures yet. */
export const fetchEaLiveStats = () => get('/api/mt5/ea-stats', {} as Record<string, EaLiveStat>);

/** Remove one .set preset from a build (admin). */
export const adminDeleteEaPreset = (id: number) =>
  adminSend<{ ok: boolean }>(`/api/admin/ea-presets/${id}`, 'DELETE');

// ---- magic number -> build mapping (Track Port live results) ----
export interface MagicRow {
  magic: number; login: string; trades: number; net: number; win: number;
  pf: number; ageSec: number;
  buildId: string | null; buildName: string | null;
  /** 'mapped' = owner set it, 'catalog' = the number matched a build's own magic */
  source: 'mapped' | 'catalog' | null;
}
export interface MagicMapView { rows: MagicRow[]; builds: { id: string; label: string }[]; }
export const adminMagics = () => get('/api/admin/mt5/magics', null as MagicMapView | null);
export const adminSetMagic = (magic: number, buildId: string | null) =>
  adminSend<{ ok: boolean }>('/api/admin/mt5/magics', 'POST', { magic, buildId });

// ---- Bubble Watch (dashboard) ----
export interface BubbleDial {
  value: number; date: string; prev: number | null; changePct: number | null;
  peak?: number; peakDate?: string; fromPeak?: number;
}
export interface BubbleGauge {
  value: number; live: boolean; date?: string;
  mean?: number; record?: number; recordWhen?: string; percentile?: number;
  dotcom?: number; since?: string; note?: string;
}
export interface BubbleView {
  asOf: string;
  gauges: { cape: BubbleGauge; buffett: BubbleGauge; top10Weight: BubbleGauge; aiShareOfGains: BubbleGauge };
  liquidity: {
    m2: BubbleDial | null; fedFunds: BubbleDial | null; balanceSheet: BubbleDial | null;
    reverseRepo: BubbleDial | null; gdp: BubbleDial | null;
    buffett: { value: number; date: string; note: string } | null;
  };
  verdict: { score: number; of: number; label: 'loose' | 'mixed' | 'tight' };
  concentration: any[]; ingredients: any[]; ipoTell: any[]; ipoProceeds: any[];
  crashes: any[]; megacaps: any[]; capeAtPeaks: any[]; hyperscalerCapex: any[];
  thenVsNow: any[]; phases: any[]; takeaways: any[];
  sources: string;
}
/**
 * Bubble Watch is paid-members-only on the server, so this reports WHY it has
 * no data rather than folding every failure into null. A free member behind the
 * blur should see the card's shape and a reason to subscribe; a member whose
 * network hiccuped should not be told they need to pay.
 */
export async function bubbleWatch(): Promise<
  { state: 'ok'; data: BubbleView } | { state: 'locked' | 'anon' | 'error' }
> {
  try {
    const res = await fetch(`${BASE}/api/bubble`, { headers: { accept: 'application/json' } });
    if (res.status === 401) return { state: 'anon' };
    if (res.status === 403) return { state: 'locked' };
    if (!res.ok) return { state: 'error' };
    return { state: 'ok', data: (await res.json()) as BubbleView };
  } catch {
    return { state: 'error' };
  }
}

// ---- NowcastLab: pre-release probability cards for US data ----
export interface NowcastCard {
  event: string;
  label: { th: string; en: string };
  generatedAt: string | null;
  generatedMs: number;
  consensus: number | null;
  consensusSource: string | null;
  nowcast: number | null;
  interval80: [number, number] | null;
  pModel: number | null;
  pKalshi: number | null;
  sentimentTilt: number | null;
  headlines: number | null;
  /** pooled probability the release comes in above consensus */
  pFinal: number;
  signal: string | null;
  components: Record<string, number> | null;
  gasolineLive: boolean | null;
  ladder: { strike: number; p: number }[] | null;
  /**
   * Expected direction and size per asset, given this release's own probability.
   * `beat`/`miss` are the historical signed averages for each outcome and
   * `expected` weights them by pFinal. null when the study has no resolved
   * events for that release.
   */
  impact: {
    name: string;
    beat: number | null; miss: number | null; expected: number;
    nBeat: number; nMiss: number; nMin: number;
    /** the same expectation in MT5 points, the price it was measured off, and where the digit count came from */
    points: number | null; refPrice: number | null;
    digits: number | null;
    digitsFrom: 'mt5' | 'default' | null;
    digitsBroker: string | null;
    digitsSymbol: string | null;
  }[] | null;
  /** Confirmed release time from the calendar feed, or null if not scheduled yet. */
  releaseAt: number | null;
  releaseTitle: string | null;
  releaseImpact: string | null;
  releaseForecast: string | null;
  /** true when the date was derived from the publication window, not confirmed by the calendar. */
  releaseEstimated?: boolean;
  /** true when the window admits only one weekday, so the derived date is the real one. */
  releaseExact?: boolean;
  releaseWindow?: [number, number];
  /** The agency's normal publication rule, shown when no date is confirmed. */
  schedule: { th: string; en: string } | null;
  /**
   * The last print this event actually made, kept beside the next forecast for
   * 24 hours after the resolver scored it. null once that day is up, or when
   * the event has no scored print yet.
   */
  lastRelease: {
    targetMonth: string | null;
    /** what the model said, what the market said, and what came out */
    predicted: number | null; consensus: number | null; actual: number;
    /** true when the print came in above consensus */
    beat: boolean | null;
    /** the probability the card published beforehand */
    pFinal: number | null;
    /** whether that probability called the direction correctly; null when unscoreable */
    called: boolean | null;
    missBy: number | null;
    resolvedAt: number; ageH: number;
  } | null;
  /**
   * The release has happened but the official figure has not reached our
   * source yet. Present only while lastRelease is null, so the two can never
   * both show. Claims nothing about the outcome.
   */
  /** the model's raw point before consensus anchoring; null when not anchored */
  nowcastModel?: number | null;
  /** how the point was combined with the market forecast (NowcastLab anchor.py) */
  anchor?: { beta: number | null; source: string; nLive: number | null; maeModel: number | null; maeConsensus: number | null } | null;
  /** pooled probability before calibration */
  pRaw?: number | null;
  awaitingResult: {
    releasedAt: number; sinceH: number;
    predicted: number | null; consensus: number | null; pFinal: number | null;
    /** false when the release date is inferred from a window, not confirmed */
    exact: boolean;
  } | null;
  /** Why impact is absent, so the card can explain rather than silently omit it. */
  impactNote: {
    reason: 'no_study' | 'one_sided';
    nBeat?: number; nMiss?: number; nEvents?: number | null;
  } | null;
}
export interface NowcastBacktest {
  n: number | null; mae: number | null; maeBenchmark: number | null; dirHit: number | null;
}
export interface NowcastAsset {
  name: string; n: number | null;
  avgAbs: number | null; medAbs: number | null; maxAbs: number | null;
  baseline: number | null; mult: number | null;
}
export interface NowcastReaction {
  generatedAt: string | null; releaseHourEt: number | null; assets: NowcastAsset[];
}
export interface NowcastCalibration {
  generatedAt: string | null; source: string; n: number | null;
  brier: number | null; brierModel: number | null; brierBaseline: number;
  hitRate: number | null;
  bins: { bin: string; n: number; pAvg: number; observed: number }[];
}
export interface NowcastCalendar {
  ok: boolean; error: string | null; coverage: string; fetchedAt: number;
}
export interface NowcastView {
  ok: boolean; error?: string;
  cards: NowcastCard[];
  backtest: Record<string, NowcastBacktest>;
  reaction: Record<string, NowcastReaction>;
  calibration: NowcastCalibration | null;
  calendar: NowcastCalendar | null;
  updated: number;
}
/**
 * Paid-members-only on the server, so this reports WHY there is no data instead
 * of folding every failure into an empty view. A free member behind the blur
 * should see what the panel covers; a member whose network dropped should not
 * be told to pay.
 */
export async function nowcast(): Promise<
  { state: 'ok'; data: NowcastView } | { state: 'locked' | 'anon' | 'error' }
> {
  try {
    const res = await fetch(`${BASE}/api/nowcast`, { headers: { accept: 'application/json' } });
    if (res.status === 401) return { state: 'anon' };
    if (res.status === 403) return { state: 'locked' };
    if (!res.ok) return { state: 'error' };
    return { state: 'ok', data: (await res.json()) as NowcastView };
  } catch {
    return { state: 'error' };
  }
}

// ---- build -> mql5.com signal (published track record) ----
export interface Mql5Signal {
  id: string; url: string; name: string;
  growth: number | null; profit: number | null; equity: number | null; balance: number | null;
  weeks: number | null; started: string | null; tradingDays: number | null;
  trades: number | null; winRate: number | null; profitFactor: number | null;
  sharpe: number | null; recovery: number | null; expectedPayoff: number | null;
  tradesPerWeek: number | null; avgHoldingTime: string | null; latestTrade: string | null;
  maxDepositLoad: number | null; longPct: number | null; shortPct: number | null;
  /** true when mql5.com could not be reached and this is the last good copy */
  stale?: boolean;
}
export interface SignalRow {
  buildId: string; label: string;
  signalId: string | null; url: string | null;
  ok: boolean | null; stale: boolean; error: string | null; ageSec: number | null;
  signal: Mql5Signal | null;
}
export const adminSignals = () => get('/api/admin/ea-signals', null as { rows: SignalRow[] } | null);
/** url '' unlinks. Rejected server-side unless the page actually loads. */
export const adminSetSignal = (buildId: string, url: string) =>
  adminSend<{ ok?: boolean; error?: string; signalId?: string; signal?: Mql5Signal; cleared?: boolean }>(
    '/api/admin/ea-signals', 'POST', { buildId, url });
/** Public: verified figures for every build that has a signal linked. */
export const eaSignals = () => get('/api/mt5/ea-signals', {} as Record<string, Mql5Signal>);

// ---- Track Port: link an MT5 account to a member ----
export interface Mt5Link {
  login: string; userId: number; label: string; created: number;
  email: string; userName: string;
}
export interface Mt5Unlinked {
  login: string; name: string; broker: string; balance: number; updatedAgoSec: number;
}
export interface Mt5LinkView { links: Mt5Link[]; unlinked: Mt5Unlinked[]; }
export const adminMt5Links = () => get('/api/admin/mt5/links', null as Mt5LinkView | null);
export const adminLinkMt5 = (body: { login: string; email: string; label?: string }) =>
  adminSend<Mt5Link>('/api/admin/mt5/links', 'POST', body);
export const adminUnlinkMt5 = (login: string) =>
  adminSend<{ ok: boolean }>(`/api/admin/mt5/links/${encodeURIComponent(login)}`, 'DELETE');
